import { forwardRef, type HTMLAttributes, type ReactNode } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;

export const DialogContent = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    hideCloseButton?: boolean;
    /** Header / scrolling body / footer layout (DialogHeader, DialogBody,
     *  DialogFooter): no padding of its own, and capped to the viewport so a
     *  tall form scrolls inside instead of running off a short screen. When
     *  even the header and buttons can't fit (a very short window), the whole
     *  pop-up scrolls rather than cutting its buttons off. */
    structured?: boolean;
  }
>(({ className, children, hideCloseButton, structured, onEscapeKeyDown, ...props }, ref) => (
  <DialogPrimitive.Portal>
    <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-background/70 backdrop-blur-sm motion-safe:data-[state=open]:animate-overlay-in" />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed left-1/2 top-1/2 z-50 w-[min(580px,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-border bg-card p-5 shadow-2xl",
        // Rises into the centre from 97% scale. `motion-safe:` skips it
        // entirely under prefers-reduced-motion. No closing animation: see
        // `dialog-in` in tailwind.config.ts.
        "motion-safe:data-[state=open]:animate-dialog-in",
        structured && "flex max-h-[calc(100dvh-2rem)] flex-col overflow-y-auto overflow-x-hidden p-0",
        className,
      )}
      onEscapeKeyDown={(e) => {
        // Escape that cancels what an input method is composing (Chinese,
        // Japanese…) belongs to the text field, not to the pop-up.
        if (e.isComposing || e.keyCode === 229) e.preventDefault();
        onEscapeKeyDown?.(e);
      }}
      {...props}
    >
      {children}
      {!hideCloseButton ? (
        <DialogPrimitive.Close className="absolute right-3 top-3 rounded-md p-1 text-muted-foreground transition hover:bg-foreground/10 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      ) : null}
    </DialogPrimitive.Content>
  </DialogPrimitive.Portal>
));
DialogContent.displayName = "DialogContent";

export const DialogTitle = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    // `text-pretty` avoids a lone fragment on the last line (a trip ID
    // split at its hyphen, "…207-" / "B?").
    className={cn("text-lg font-bold tracking-tight text-pretty", className)}
    {...props}
  />
));
DialogTitle.displayName = "DialogTitle";

export const DialogDescription = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
DialogDescription.displayName = "DialogDescription";

const HEADER_TONE = {
  primary: "bg-primary/10 text-blue-700 ring-primary/25 dark:text-primary",
  danger: "bg-rose-500/10 text-rose-600 ring-rose-500/25 dark:text-rose-300",
} as const;

/** Title block of a `structured` pop-up: a tinted icon tile beside the
 *  title and description. */
export function DialogHeader({
  icon: Icon,
  tone = "primary",
  className,
  children,
}: {
  icon: LucideIcon;
  tone?: keyof typeof HEADER_TONE;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn("flex items-start gap-3.5 px-5 pb-5 pt-6 sm:px-6", className)}>
      <span
        aria-hidden="true"
        className={cn(
          "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ring-1 ring-inset",
          HEADER_TONE[tone],
        )}
      >
        <Icon className="h-5 w-5" />
      </span>
      <div className="flex min-w-0 flex-col gap-1 pt-px">{children}</div>
    </div>
  );
}

/** The part of a `structured` pop-up that scrolls when it can't fit. Radix's
 *  focus trap wraps Tab (footer back to the first field) with preventScroll,
 *  which would leave that field hidden above the fold, so a control that
 *  takes focus in here is brought into view. It starts 4px higher with 4px of
 *  top padding, so the scroll edge doesn't clip a first-row focus ring. */
export function DialogBody({ className, onFocus, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("-mt-1 min-h-0 flex-1 overflow-y-auto px-5 pb-6 pt-1 sm:px-6", className)}
      onFocus={(e) => {
        const target = e.target as HTMLElement;
        if (e.currentTarget.contains(target)) target.scrollIntoView?.({ block: "nearest" });
        onFocus?.(e);
      }}
      {...props}
    />
  );
}

/** Action bar of a `structured` pop-up, set off by a hairline. */
export function DialogFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "flex flex-wrap items-center justify-end gap-2 border-t border-border/70 bg-foreground/[0.025] px-5 py-4 sm:px-6",
        className,
      )}
      {...props}
    />
  );
}
